import { jsonParse } from '@lyearn/core/utils/json';
import { TFunction } from '@lyearn/i18n';
import { ArrowUp, FlagOutline } from '@lyearn/icons';
import { FUTURE_DATE_FILTER_ITEMS } from '@/components/DateFilter/constants';
import { FilterGroup } from '@/components/Filter/types';
import { tagsFilterConfig } from '@/components/FilterManager/configs/tags/tagsFilterConfig';
import { GoalEntityConfig } from '@/components/FilterManager/EntityFilter/entity/goals';
import { ProjectStageEntityConfig } from '@/components/FilterManager/EntityFilter/entity/project-stage';
import {
  MindsetEntityConfig,
  SkillEntityConfig,
} from '@/components/FilterManager/EntityFilter/entity/skills';
import { UserEntityConfig } from '@/components/FilterManager/EntityFilter/entity/user';
import { SortMenuItemType } from '@/components/FilterManager/SortMenu/types';
import {
  getAlphabeticallySortItem,
  getDefaultSortValue,
  getDueDateSortItem,
  getLastEditedSortItem,
  getNewestSortItem,
  getOldestSortItem,
} from '@/components/FilterManager/SortMenu/utils';
import {
  DateRangeItem,
  FilterItem,
  FilterManager,
  SortAndFilterItem,
  SortEnums,
  SortItem,
} from '@/components/FilterManager/types';
import { getReporteeFilterItems } from '@/modules/learner/views/TeamDashboard/utils';
import { ACTION_ITEM_PRIORITY_CONFIG } from '@/modules/tasks/features/actionItems/components/ActionPriority/utils';
import FileEntity from '@/modules/tasks/file.entity';
import {
  OrderEnum,
  ReporteeFilterTypeEnum,
  TagAvailableOnEnum,
  UserTypeEnum,
} from '@/types/schema';

import TaskGroupByFilter from '../components/TaskGroupByFilter';
import { FileFragment } from '../graphql/fragments/generated/file';

const orderSortItem: SortMenuItemType = {
  Icon: ArrowUp,
  id: SortEnums.Order,
  label: 'Order',
  sort: [{ field: 'order', order: OrderEnum.Asc }],
};

const prioritySortItem: SortMenuItemType = {
  Icon: FlagOutline,
  id: 'priority',
  label: 'Priority',
  sort: [{ field: 'priority', order: OrderEnum.Asc }],
};

export const TASK_SORT_ITEMS: SortMenuItemType[] = [
  // orderSortItem,
  getNewestSortItem(),
  getOldestSortItem(),
  getLastEditedSortItem(),
  getAlphabeticallySortItem({ sort: [{ field: 'title', order: OrderEnum.Asc }] }),
  getDueDateSortItem(),
  prioritySortItem,
];

export const TASK_SORT_DEFAULT_VALUE = getDefaultSortValue(TASK_SORT_ITEMS);

export const getTaskPrioritiesGroups = (t: TFunction) => {
  return [
    {
      id: 'priorities',
      options: Object.values(ACTION_ITEM_PRIORITY_CONFIG).map((config) => ({
        value: config.value,
        Icon: config.Icon,
        label: t(config.labelKey),
      })),
    },
  ];
};

export const getTaskSortItem = (): SortItem => {
  return {
    type: FilterManager.Sort,
    isEnabled: true,
    items: TASK_SORT_ITEMS,
  };
};

export const getTaskStatusItem = (t: TFunction, statusFilterGroup: FilterGroup[]): FilterItem => {
  return {
    type: FilterManager.Filter,
    id: 'statusIds',
    defaultPlaceholder: t('Common:Filter.Status'),
    isEnabled: true,
    groups: statusFilterGroup,
  };
};

export const getTaskPriorityItem = (t: TFunction): FilterItem => {
  return {
    type: FilterManager.Filter,
    id: 'priorities',
    defaultPlaceholder: t('Common:Filter.Priority'),
    isEnabled: true,
    groups: getTaskPrioritiesGroups(t),
  };
};

export const getTaskDueDateItem = (t: TFunction): DateRangeItem => {
  return {
    type: FilterManager.DateRange,
    id: 'dueDate',
    defaultPlaceholder: t('Common:Filter.DueDate'),
    options: FUTURE_DATE_FILTER_ITEMS,
    isEnabled: true,
  };
};

export default function getSortAndFilterItems({
  t,
  currentUserId,
  file,
  taskViewConfigs,
  reporteeFilterType,
  statusFilterGroup,
}: {
  t: TFunction;
  currentUserId: string;
  file?: FileFragment;
  taskViewConfigs?: FileFragment['taskViewConfigs'];
  reporteeFilterType?: ReporteeFilterTypeEnum;
  statusFilterGroup: FilterGroup[];
}): SortAndFilterItem[] {
  const type = (jsonParse(file?.meta || '{}').data as any).type;

  return [
    {
      type: FilterManager.SingleSelectFilter,
      id: 'reporteeFilterType',
      defaultPlaceholder: '',
      defaultSelectedValue: ReporteeFilterTypeEnum.DirectReportee,
      isEnabled: type === 'Team Tasks',
      hideOperator: true,
      appearance: 'primary',
      items: getReporteeFilterItems(t),
    },
    {
      type: FilterManager.Divider,
      isEnabled: type === 'Team Tasks',
    },
    getTaskSortItem(),
    {
      type: FilterManager.Custom,
      renderer: ({ filter, setFilter }) => (
        <TaskGroupByFilter
          file={file}
          filter={filter}
          setFilter={setFilter}
          taskViewConfigs={taskViewConfigs}
        />
      ),
      isEnabled: true,
    },
    getTaskStatusItem(t, statusFilterGroup),
    getTaskPriorityItem(t),
    getTaskDueDateItem(t),
    {
      type: FilterManager.Entity,
      isEnabled: type !== 'My Tasks' && type !== 'Team Tasks',
      id: 'assignedToIds',
      defaultPlaceholder: t('Common:Filter.Assignee'),
      listConfig: UserEntityConfig,
      entity: 'user',
      queryParams: {
        userTypes: [UserTypeEnum.Learner],
        ids: (jsonParse(file?.filterQuery || '{}').data as any).assignedToIds,
      },
    },
    {
      type: FilterManager.Entity,
      isEnabled: type === 'Team Tasks',
      id: 'assignedToIds',
      defaultPlaceholder: t('Common:Filter.Assignee'),
      listConfig: UserEntityConfig,
      entity: 'user',
      queryParams: {
        parentId:
          reporteeFilterType !== ReporteeFilterTypeEnum.InDirectReportee
            ? currentUserId
            : undefined,
        hierarchyPath:
          reporteeFilterType === ReporteeFilterTypeEnum.InDirectReportee
            ? currentUserId
            : undefined,
      },
    },
    {
      type: FilterManager.Entity,
      isEnabled: !!file && FileEntity.shouldShowStages(file),
      id: 'stageIds',
      defaultPlaceholder: t('Task:Filter.Stage'),
      listConfig: ProjectStageEntityConfig,
      queryParams: {
        projectId: file?.id,
      },
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'skillIds',
      defaultPlaceholder: t('Common:Filter.Skills'),
      entity: 'skill',
      listConfig: SkillEntityConfig,
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'mindsetIds',
      defaultPlaceholder: t('Common:Filter.Mindsets'),
      listConfig: MindsetEntityConfig,
    },
    {
      type: FilterManager.Entity,
      isEnabled: true,
      id: 'goalIds',
      defaultPlaceholder: t('Common:Filter.Goal'),
      listConfig: GoalEntityConfig,
    },
    {
      ...tagsFilterConfig(t),
      queryParams: {
        availableOn: [TagAvailableOnEnum.Task],
        showProviders: true,
      },
      headerProps: {},
    },
  ];
}
